import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

interface ArticleLayoutProps {
  title: string;
  description: string;
  heroImage: string;
  heroImageAlt: string;
  pageUrl: string;
  datePublished?: string;
  dateModified?: string;
  children: ReactNode;
  relatedArticles: { title: string; path: string }[];
}

export default function ArticleLayout({ title, description, heroImage, heroImageAlt, pageUrl, datePublished = '2026-07-16', dateModified = '2026-07-16', children, relatedArticles }: ArticleLayoutProps) {
  const fullUrl = `https://oopbuyspread.baby${pageUrl}`;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": title,
                "description": description,
                "image": heroImage,
                "url": fullUrl,
                "inLanguage": "de",
                "datePublished": datePublished,
                "dateModified": dateModified,
                "author": {
                  "@type": "Organization",
                  "name": "OOPBUY Spreadsheet Redaktion",
                  "url": "https://oopbuyspread.baby"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "OOPBUY Spreadsheet Deutschland",
                  "url": "https://oopbuyspread.baby",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg"
                  }
                },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": fullUrl
                }
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Startseite",
                    "item": "https://oopbuyspread.baby"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": title
                  }
                ]
              }
            ]
          })
        }}
      />
      <nav className="sticky top-0 z-50 w-full px-4 md:px-12 py-4 bg-white/95 backdrop-blur-sm border-b border-background-200/70">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4 md:gap-6">
            <Link to="/" className="text-foreground-900 font-heading text-base md:text-lg font-bold tracking-tight whitespace-nowrap cursor-pointer">OOPBUY Spreadsheet</Link>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/" className="text-foreground-500 text-sm hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Startseite</Link>
              <Link to="/oopbuy-spreadsheet-guide" className="text-foreground-500 text-sm hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">Shopping Guide</Link>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-foreground-600 text-xs md:text-sm hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
              <span className="w-4 h-4 flex items-center justify-center inline mr-1">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              Zurück
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 md:px-5 py-2 md:py-2.5 bg-primary-500 text-white text-xs md:text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </a>
          </div>
        </div>
      </nav>

      <main>
        <div className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
          <img src={heroImage} alt={heroImageAlt} className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-12 lg:px-20 pb-8 md:pb-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 mb-3">
                <Link to="/" className="text-white/70 text-xs hover:text-white transition-colors whitespace-nowrap cursor-pointer">Startseite</Link>
                <span className="text-white/40 text-xs">/</span>
                <span className="text-white/80 text-xs font-medium whitespace-nowrap">{title.split('–')[0]?.trim() || title}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white leading-tight">{title}</h1>
            </div>
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-12 py-8 md:py-14">
          <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-10 border-l-4 border-accent-400 pl-4">
            {description}
          </p>

          <div className="prose-custom">
            {children}
          </div>

          <div className="mt-14 p-5 md:p-7 bg-background-100 rounded-xl border border-background-200/70">
            <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950 mb-4">Verwandte Artikel</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {relatedArticles.map((article) => (
                <Link
                  key={article.path}
                  to={article.path}
                  className="flex items-center gap-2 p-3 bg-white rounded-lg text-sm text-foreground-700 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-article-line text-accent-500"></i>
                  </span>
                  {article.title}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8 p-5 md:p-7 bg-primary-50 rounded-xl border border-primary-200/50">
            <h3 className="text-base md:text-lg font-heading font-bold text-foreground-950 mb-3">Bereit zum Shoppen?</h3>
            <p className="text-foreground-600 text-sm leading-relaxed mb-4">
              Jetzt, wo du mehr über OOPBUY Spreadsheet weißt, entdecke die aktuellen Produktlisten und besten Angebote auf dem Hauptportal.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                oopbuy spreadsheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
              <Link to="/" className="text-foreground-600 text-sm hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
                &larr; Zurück zur Startseite
              </Link>
            </div>
          </div>
        </article>
      </main>

      <footer className="px-4 md:px-12 py-8 bg-foreground-950">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/" className="text-white/60 text-sm hover:text-white transition-colors whitespace-nowrap cursor-pointer">Startseite</Link>
            <Link to="/oopbuy-spreadsheet-guide" className="text-white/60 text-sm hover:text-white transition-colors whitespace-nowrap cursor-pointer">Shopping Guide</Link>
          </div>
          <p className="text-white/30 text-xs whitespace-nowrap">
            &copy; 2026 OOPBUY Spreadsheet. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </>
  );
}